// frontend/src/components/AppUpdateNotification.js
import React, { useState, useEffect, useCallback } from 'react';
import { Button, notification, Space, theme } from 'antd'; // Added notification, Space and theme
import { DownloadOutlined, ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import * as serviceWorkerRegistration from '../serviceWorkerRegistration';
import { useNotification } from '../context/NotificationContext';

const AppUpdateNotification = () => {
    const { notification: notify, modal } = useNotification();
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    
    // State to hold the Service Worker registration object when an update is found
    const [waitingWorker, setWaitingWorker] = useState(null);
    
    // --- PWA Installation Prompt Logic (Kept for completeness) ---
    useEffect(() => {
        const handler = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        window.addEventListener('beforeinstallprompt', handler);
        return () => {
            window.removeEventListener('beforeinstallprompt', handler);
        };
    }, []);

    const handleInstallClick = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        await deferredPrompt.userChoice;
        setDeferredPrompt(null);
    };
    // ------------------------------------------------------------


    // --- Service Worker Update Logic ---
    const updateApp = useCallback(() => {
        if (!waitingWorker) {
            // Fallback: just reload if we don't have the worker reference but somehow got here
            window.location.reload();
            return;
        }

        // 1. Listen for the new service worker to take control (triggered by skipWaiting)
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            window.location.reload();
        });

        // 2. Tell the Service Worker to skip waiting and activate
        waitingWorker.postMessage({ type: 'SKIP_WAITING' });
        
        // 3. Robust fallback: reload anyway if controllerchange hasn't fired after 1 second
        setTimeout(() => {
            window.location.reload();
        }, 1000);
        
    }, [waitingWorker]);

    const { token } = theme.useToken();

    const showUpdateNotification = useCallback((registration) => {
        // The waiting worker is the new version of the SW
        const worker = registration.waiting;
        if (worker) {
            setWaitingWorker(worker);
            
            const key = `update-${new Date().getTime()}`;
            
            const btn = (
                <Button 
                    type="primary" 
                    icon={<ReloadOutlined />} 
                    onClick={() => {
                        // Crucial: call updateApp first, then destroy notification
                        // In case updateApp fails to trigger reload immediately
                        updateApp();
                        notify.destroy(key);
                    }}
                >
                    Update & Reload Now
                </Button>
            );

            notify.info({
                message: 'Application Update Ready',
                description: 'A new version with latest improvements is available. Please reload to apply changes.',
                btn,
                key,
                duration: 0,
                placement: 'bottomRight',
                icon: <InfoCircleOutlined style={{ color: token.colorPrimary }} />
            });
        }
    }, [notify, updateApp, token]);
    // ------------------------------------------------------------


    useEffect(() => {
        // Register the service worker with the update callback
        serviceWorkerRegistration.register({
            onUpdate: showUpdateNotification,
        });
    }, [showUpdateNotification]);


    const isAppInstalled = window.matchMedia('(display-mode: standalone)').matches;

    return (
        <>
            {/* Show PWA Install Button if deferredPrompt is available and app is not installed */}
            {!isAppInstalled && deferredPrompt && (
                <Button 
                    type="default" 
                    icon={<DownloadOutlined />} 
                    onClick={handleInstallClick}
                    style={{ position: 'fixed', bottom: '80px', right: '20px', zIndex: 1000 }}
                >
                    Install App
                </Button>
            )}
            {/* The update notification is handled by the AntD notification system */}
        </>
    );
};

export default AppUpdateNotification;